/**
 * PRODUCT-SPECIFICATIONS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * specExport — a branded native-.xlsx export of the product specifications grid,
 * formatted with the COMPANY's brand colors (account primary/secondary) and the
 * company logo. Mirrors the depletions/forecast export look. ExcelJS is loaded
 * on demand so it stays out of the main bundle. We render data values only (no
 * user HTML), so there is no injection surface — ExcelJS escapes cell text.
 */

import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";
import { supabase } from "../../../shared/utils/supabaseClient.js";

// App default palette (used when the company hasn't set brand colors).
// ACCENT is the app orange (backlog 145/166; was the retired #E85D26). Its only
// use here is a solid 4px divider bar with no text on it, so this is the FILL
// role — do not reuse it as a font color on white, where it measures 1.96:1.
const NAVY = "FF1B2A4A", ACCENT = "FFF2A33C";
const C = { border: "FFD4DCEA", white: "FFFFFFFF", text: "FF1B2A4A", muted: "FF6B7A99", altRow: "FFFBFCFE" };
const THIN = { style: "thin", color: { argb: C.border } };
const ALL_BORDERS = { top: THIN, left: THIN, bottom: THIN, right: THIN };

// "#rrggbb" -> "FFrrggbb" (Excel ARGB). Invalid -> fallback argb.
function hexToArgb(hex, fallback) {
  if (typeof hex === "string" && /^#[0-9a-fA-F]{6}$/.test(hex.trim())) {
    return "FF" + hex.trim().slice(1).toUpperCase();
  }
  return fallback;
}

// Black/white text for legibility on a colored fill (argb "FFrrggbb").
function readableOn(argb) {
  const r = parseInt(argb.slice(2, 4), 16), g = parseInt(argb.slice(4, 6), 16), b = parseInt(argb.slice(6, 8), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? C.text : C.white;
}

function fetchAsDataUrl(url) {
  return fetch(url, { mode: "cors" })
    .then((r) => { if (!r.ok) throw new Error(`logo ${r.status}`); return r.blob(); })
    .then((blob) => new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    }));
}

// The account logo, or null. Backlog 194: this used to fall back to the Brand
// Command mark whenever the tenant had no logo or the fetch failed, which put
// OUR logo on a spec sheet the tenant hands to their buyer — i.e. their
// vendor's brand on their document. A missing/unfetchable logo now means no
// logo at all, and the caller is told so it can warn.
async function resolveLogo(logoUrl) {
  if (!logoUrl) return null;
  try { return await fetchAsDataUrl(logoUrl); } catch { return null; }
}

// Rasterize ANY logo (svg/png/jpg/webp) to a fitted PNG — embedding original
// bytes is what triggers Excel's "this picture can't be displayed".
function rasterizeToPng(dataUrl, maxW, maxH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth || maxW, nh = img.naturalHeight || maxH;
      const scale = Math.min(maxW / nw, maxH / nh, 1);
      const width = Math.max(1, Math.round(nw * scale));
      const height = Math.max(1, Math.round(nh * scale));
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve({ base64: canvas.toDataURL("image/png").split(",")[1], width, height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

async function actorName() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "—";
  } catch { return "—"; }
}

/**
 * @param {Object} opts
 * @param {Array}  opts.columns  [{ label, key, kind:'dim'|'num'|'img' }]
 *                 'img' cells embed a photo from a (signed) URL in row[key].
 * @param {Array}  opts.rows     array of objects keyed by column.key
 * @param {string} [opts.title]
 * @returns {Promise<{ logo: 'ok'|'none'|'failed' }>}
 *   `logo` lets the caller warn in the export dialog when the workbook went out
 *   without a mark (backlog 194).
 */
export async function exportProductSpecs({ columns, rows, title = "Product Specifications" }) {
  const ExcelJS = (await import("exceljs")).default;

  let teamName = "", logoUrl = null, primary = NAVY, accent = ACCENT;
  let acctFailed = false;
  try {
    const acct = await getMyPrimaryAccount();
    teamName = acct?.name ?? "";
    logoUrl = acct?.logoUrl ?? null;
    primary = hexToArgb(acct?.primaryColor, NAVY);
    accent = hexToArgb(acct?.secondaryColor, ACCENT);
  } catch { acctFailed = true; /* unbranded */ }

  const headerText = readableOn(primary);
  const who = await actorName();
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const wb = new ExcelJS.Workbook();
  wb.creator = "Brand Command";
  wb.created = now;
  const ws = wb.addWorksheet("Product Specs", { views: [{ showGridLines: false }] });

  const colCount = columns.length;
  columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width || (c.kind === "num" ? 14 : 20); });

  const mergeRow = (r, text, style = {}) => {
    ws.mergeCells(r, 1, r, colCount);
    const cell = ws.getCell(r, 1);
    cell.value = text;
    cell.font = style.font || {};
    if (style.fill) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: style.fill } };
    cell.alignment = style.alignment || { vertical: "middle", horizontal: "left" };
    if (style.height) ws.getRow(r).height = style.height;
  };

  // ── Branded header ──
  // The logo row only exists when there IS a tenant logo — with no mark to
  // place, the sheet starts at the title instead of leaving a 46pt blank band
  // where ours used to sit (backlog 194).
  let logoStatus = logoUrl || acctFailed ? "failed" : "none";
  let r = 1;
  try {
    const dataUrl = await resolveLogo(logoUrl);
    const png = dataUrl ? await rasterizeToPng(dataUrl, 230, 48) : null;
    if (png) {
      ws.getRow(1).height = 46;
      const imgId = wb.addImage({ base64: png.base64, extension: "png" });
      ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: png.width, height: png.height } });
      logoStatus = "ok";
      r = 2;
    }
  } catch { /* no logo block */ }

  mergeRow(r++, title, { font: { name: "Inter", size: 16, bold: true, color: { argb: primary } } });
  if (teamName) mergeRow(r++, teamName, { font: { name: "Inter", size: 12, bold: true, color: { argb: primary } } });
  mergeRow(r++, "", { fill: accent, height: 4 });
  mergeRow(r++, `Exported: ${dateStr} at ${timeStr}     ·     Exported by: ${who}     ·     ${rows.length} SKU${rows.length === 1 ? "" : "s"}`,
    { font: { name: "Inter", size: 10, color: { argb: C.muted } } });
  ws.getRow(r++).height = 6;

  // ── Table header (company primary) ──
  const headerRow = r;
  columns.forEach((c, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = String(c.label ?? "").toUpperCase();
    cell.font = { name: "Inter", size: 11, bold: true, color: { argb: headerText } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: primary } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = ALL_BORDERS;
  });
  r++;
  ws.views = [{ state: "frozen", xSplit: 1, ySplit: headerRow, showGridLines: false }];

  // ── Data rows ── (img cells embed a fitted photo, sizing the row to fit)
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const bg = i % 2 ? null : C.altRow;
    let rowImgH = 0;
    for (let ci = 0; ci < columns.length; ci++) {
      const c = columns[ci];
      const cell = ws.getCell(r, ci + 1);
      if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
      cell.border = ALL_BORDERS;

      if (c.kind === "img") {
        cell.value = null;
        const url = row[c.key];
        if (url) {
          try {
            const dataUrl = await fetchAsDataUrl(url);
            const png = await rasterizeToPng(dataUrl, 150, 60);
            if (png) {
              const imgId = wb.addImage({ base64: png.base64, extension: "png" });
              ws.addImage(imgId, { tl: { col: ci + 0.15, row: (r - 1) + 0.15 }, ext: { width: png.width, height: png.height } });
              rowImgH = Math.max(rowImgH, png.height);
            }
          } catch { /* skip a broken/expired image */ }
        }
        continue;
      }

      if (c.kind === "active") {
        cell.value = row[c.key] ? "Yes" : "No";
        cell.alignment = { horizontal: "center" };
        cell.font = { name: "Inter", size: 10, color: { argb: C.text } };
        continue;
      }

      const v = row[c.key];
      if (c.kind === "num") {
        cell.value = v === "" || v == null ? null : Number(v);
        cell.alignment = { horizontal: "right" };
      } else {
        cell.value = v ?? "";
        cell.alignment = { horizontal: "left" };
      }
      cell.font = { name: "Inter", size: 10, bold: ci === 0, color: { argb: C.text } };
    }
    if (rowImgH) ws.getRow(r).height = Math.max(ws.getRow(r).height || 0, rowImgH * 0.75 + 8);
    r++;
  }

  if (!rows.length) mergeRow(r++, "No products yet.", { font: { name: "Inter", size: 11, color: { argb: C.muted } }, alignment: { horizontal: "center" } });

  // ── Download ──
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const team = (teamName || "team").trim() || "team";
  const filename = `${team}_Product_Specs_${now.toISOString().slice(0, 10)}.xlsx`.replace(/[^A-Za-z0-9._-]+/g, "_");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);

  return { logo: logoStatus };
}
